// ============================================================================
// useZoomPan — focal-point zoom + pan for the crop image viewer
//
// Zoom: scroll-wheel (focal-point, no modifier needed) or +/− buttons
// Pan: Space + drag (any zoom level >1x), or Shift + scroll wheel
// All state is CSS-transform-based; crop coordinates are unaffected.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { clamp } from '../utils/cropUtils';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const WHEEL_SENSITIVITY = 0.002; // per deltaY pixel

export interface ZoomPanState {
  zoom: number;
  panX: number; // px offset (in un-zoomed space)
  panY: number;
}

export interface ZoomPanActions {
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  /** Callback ref — attach to the outer clip container via ref={} */
  setContainerRef: (node: HTMLDivElement | null) => void;
  /** Attach to the zoom wrapper for Space+drag panning */
  handlePanPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePanPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePanPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  isPanning: boolean;
  /** Whether Space is held (components can use this to show grab cursor) */
  isSpaceHeld: boolean;
}

/**
 * @param isActive - pass `true` when the modal is open so keyboard listeners
 *   activate. The wheel listener uses a callback ref and self-manages.
 */
export function useZoomPan(isActive: boolean): [ZoomPanState, ZoomPanActions] {
  const [state, setState] = useState<ZoomPanState>({ zoom: 1, panX: 0, panY: 0 });
  const stateRef = useRef(state);
  stateRef.current = state;

  // Store the container DOM node and the cleanup for the wheel listener
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const wheelCleanupRef = useRef<(() => void) | null>(null);

  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  const spaceRef = useRef(false);

  // Track Space key for pan mode
  useEffect(() => {
    if (!isActive) return;
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !(e.target as HTMLElement).matches('input,textarea,select')) {
        e.preventDefault();
        spaceRef.current = true;
        setIsSpaceHeld(true);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceRef.current = false;
        setIsSpaceHeld(false);
      }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      spaceRef.current = false;
      setIsSpaceHeld(false);
    };
  }, [isActive]);

  // Clamp pan so image doesn't leave viewport entirely
  const clampPan = useCallback((panX: number, panY: number, zoom: number) => {
    const container = containerNodeRef.current;
    if (!container) return { panX, panY };
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const maxPanX = (cw * (zoom - 1)) / (2 * zoom);
    const maxPanY = (ch * (zoom - 1)) / (2 * zoom);
    return {
      panX: clamp(panX, -maxPanX, maxPanX),
      panY: clamp(panY, -maxPanY, maxPanY),
    };
  }, []);

  // Zoom toward a focal point (in container-relative px)
  const zoomToward = useCallback((newZoom: number, focalX: number, focalY: number) => {
    setState((prev) => {
      const clamped = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
      if (clamped === prev.zoom) return prev;

      const zoomRatio = clamped / prev.zoom;
      const newPanX = focalX - zoomRatio * (focalX - prev.panX);
      const newPanY = focalY - zoomRatio * (focalY - prev.panY);
      const panned = clampPan(newPanX, newPanY, clamped);
      return { zoom: clamped, ...panned };
    });
  }, [clampPan]);

  // Create the wheel handler (captures stateRef/zoomToward via closure)
  const attachWheel = useCallback((node: HTMLDivElement) => {
    const handler = (e: WheelEvent) => {
      const s = stateRef.current;

      // Shift + wheel → pan horizontally
      if (e.shiftKey && s.zoom > 1) {
        e.preventDefault();
        const dx = -e.deltaY / s.zoom;
        const panned = clampPan(s.panX + dx, s.panY, s.zoom);
        setState((prev) => ({ ...prev, ...panned }));
        return;
      }

      // Regular wheel → focal-point zoom
      e.preventDefault();
      e.stopPropagation();
      const rect = node.getBoundingClientRect();
      const focalX = (e.clientX - rect.left - rect.width / 2) / s.zoom;
      const focalY = (e.clientY - rect.top - rect.height / 2) / s.zoom;

      const delta = -e.deltaY * WHEEL_SENSITIVITY;
      const newZoom = clamp(s.zoom * (1 + delta), MIN_ZOOM, MAX_ZOOM);
      zoomToward(newZoom, focalX, focalY);
    };

    node.addEventListener('wheel', handler, { passive: false });
    return () => node.removeEventListener('wheel', handler);
  }, [zoomToward, clampPan]);

  // Callback ref — fires when the DOM node mounts/unmounts
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous
    if (wheelCleanupRef.current) {
      wheelCleanupRef.current();
      wheelCleanupRef.current = null;
    }
    containerNodeRef.current = node;
    if (node) {
      wheelCleanupRef.current = attachWheel(node);
    }
  }, [attachWheel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wheelCleanupRef.current) {
        wheelCleanupRef.current();
        wheelCleanupRef.current = null;
      }
    };
  }, []);

  // Button zoom (toward center)
  const zoomIn = useCallback(() => {
    zoomToward(clamp(stateRef.current.zoom + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM), 0, 0);
  }, [zoomToward]);

  const zoomOut = useCallback(() => {
    zoomToward(clamp(stateRef.current.zoom - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM), 0, 0);
  }, [zoomToward]);

  const resetZoom = useCallback(() => {
    setState({ zoom: 1, panX: 0, panY: 0 });
  }, []);

  // Space + drag pan (works regardless of crop state, as long as zoomed >1x)
  const handlePanPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!spaceRef.current || stateRef.current.zoom <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: stateRef.current.panX,
      panY: stateRef.current.panY,
    };
    setIsPanning(true);
  }, []);

  const handlePanPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = panStartRef.current;
    if (!start) return;
    const s = stateRef.current;
    const dx = (e.clientX - start.x) / s.zoom;
    const dy = (e.clientY - start.y) / s.zoom;
    const panned = clampPan(start.panX + dx, start.panY + dy, s.zoom);
    setState((prev) => ({ ...prev, ...panned }));
  }, [clampPan]);

  const handlePanPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (panStartRef.current) {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      panStartRef.current = null;
      setIsPanning(false);
    }
  }, []);

  return [
    state,
    {
      zoomIn,
      zoomOut,
      resetZoom,
      setContainerRef,
      handlePanPointerDown,
      handlePanPointerMove,
      handlePanPointerUp,
      isPanning,
      isSpaceHeld,
    },
  ];
}
